import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
var _excluded = ["src", "alt", "width", "height", "quality", "cropParams", "templates", "responsive", "lazy", "nativeLazy", "lazyThreshold", "lazyWrapElement", "className", "avifIsEnabled", "avifQuality", "fallbackFmt", "data-test", "additionalMedia"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import React, { useState, useMemo, Fragment } from 'react';
import PropTypes from 'prop-types';
import { InView } from 'react-intersection-observer';
import { canUseDom } from '@sainsburys-tech/boltui-utils';

var ImageSrcFormat = function ImageSrcFormat(src, width, height, quality, cropParams, templates, fallbackFmt, avifQuality) {
  var supportedResolutions = [1, 2];
  var srcSet = '';
  var srcSetJpeg = '';
  var srcSetAvif = '';
  var cropParam = typeof cropParams !== 'undefined' ? "pcrop=".concat(cropParams.xCoord, ",").concat(cropParams.yCoord, ",").concat(cropParams.cropWidth, ",").concat(cropParams.cropHeight, "&") : '';
  var templatesParam = templates.length ? "".concat(templates.join('&'), "&") : "";
  supportedResolutions.forEach(function (res) {
    var widthParam = typeof width !== 'undefined' ? "w=".concat(parseFloat(width) * res, "&") : "";
    var heightParam = typeof height !== 'undefined' ? "h=".concat(height * res, "&") : "";
    srcSet += "".concat(src, "?").concat(templatesParam).concat(widthParam).concat(heightParam).concat(cropParam, "qlt=").concat(quality, "&fmt=webp ").concat(res, "x, ");
    srcSetJpeg += "".concat(src, "?").concat(templatesParam).concat(widthParam).concat(heightParam).concat(cropParam, "qlt=").concat(quality, "&fmt.jpeg.interlaced=true ").concat(res, "x, ");
    srcSetAvif += "".concat(src, "?").concat(templatesParam).concat(widthParam).concat(heightParam).concat(cropParam, "qlt=").concat(avifQuality || quality, "&fmt=avif ").concat(res, "x, ");
  });
  var widthParam = typeof width !== 'undefined' ? "w=".concat(width, "&") : "";
  var heightParam = typeof height !== 'undefined' ? "h=".concat(height, "&") : "";
  return {
    src: "".concat(src, "?").concat(templatesParam).concat(widthParam).concat(heightParam).concat(cropParam, "qlt=").concat(quality, "&").concat(fallbackFmt),
    srcSet: srcSet,
    srcSetJpeg: srcSetJpeg,
    srcSetAvif: srcSetAvif
  };
};

var LazyWrapper = function LazyWrapper(_ref) {
  var lazy = _ref.lazy,
      nativeLazy = _ref.nativeLazy,
      lazyThreshold = _ref.lazyThreshold,
      lazyWrapElement = _ref.lazyWrapElement,
      children = _ref.children;

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      isInView = _useState2[0],
      toggleInView = _useState2[1];

  if (!lazy) return children;

  if (!nativeLazy || nativeLazy && canUseDom() && !('loading' in HTMLImageElement.prototype)) {
    return /*#__PURE__*/React.createElement(InView, {
      as: lazyWrapElement,
      rootMargin: "200px 0px",
      threshold: lazyThreshold,
      onChange: function onChange(inView) {
        return toggleInView(inView);
      },
      triggerOnce: true
    }, isInView && children);
  }

  return children;
};

var Image = function Image(_ref2) {
  var src = _ref2.src,
      alt = _ref2.alt,
      width = _ref2.width,
      height = _ref2.height,
      _ref2$quality = _ref2.quality,
      quality = _ref2$quality === void 0 ? 75 : _ref2$quality,
      cropParams = _ref2.cropParams,
      _ref2$templates = _ref2.templates,
      templates = _ref2$templates === void 0 ? [] : _ref2$templates,
      _ref2$responsive = _ref2.responsive,
      responsive = _ref2$responsive === void 0 ? false : _ref2$responsive,
      _ref2$lazy = _ref2.lazy,
      lazy = _ref2$lazy === void 0 ? false : _ref2$lazy,
      _ref2$nativeLazy = _ref2.nativeLazy,
      nativeLazy = _ref2$nativeLazy === void 0 ? false : _ref2$nativeLazy,
      _ref2$lazyThreshold = _ref2.lazyThreshold,
      lazyThreshold = _ref2$lazyThreshold === void 0 ? 0.5 : _ref2$lazyThreshold,
      _ref2$lazyWrapElement = _ref2.lazyWrapElement,
      lazyWrapElement = _ref2$lazyWrapElement === void 0 ? 'div' : _ref2$lazyWrapElement,
      className = _ref2.className,
      _ref2$avifIsEnabled = _ref2.avifIsEnabled,
      avifIsEnabled = _ref2$avifIsEnabled === void 0 ? false : _ref2$avifIsEnabled,
      _ref2$avifQuality = _ref2.avifQuality,
      avifQuality = _ref2$avifQuality === void 0 ? 50 : _ref2$avifQuality,
      _ref2$fallbackFmt = _ref2.fallbackFmt,
      fallbackFmt = _ref2$fallbackFmt === void 0 ? 'fmt.jpeg.interlaced=true' : _ref2$fallbackFmt,
      _ref2$dataTest = _ref2['data-test'],
      dataTest = _ref2$dataTest === void 0 ? 'component-image' : _ref2$dataTest,
      additionalMedia = _ref2.additionalMedia,
      props = _objectWithoutProperties(_ref2, _excluded);

  var _ImageSrcFormat = ImageSrcFormat(src, width, height, quality, cropParams, templates, fallbackFmt, avifQuality),
      imageSrc = _ImageSrcFormat.src,
      srcSet = _ImageSrcFormat.srcSet,
      srcSetJpeg = _ImageSrcFormat.srcSetJpeg,
      srcSetAvif = _ImageSrcFormat.srcSetAvif; // Map over the additional media (if provided) and build srcSets for each of them.
  // Memoise this, in case there's lots of additional media.


  var mediaWithSources = useMemo(function () {
    if (!Array.isArray(additionalMedia)) return [];
    return additionalMedia.map(function (media) {
      return _objectSpread({
        sources: ImageSrcFormat(media.src, media.width, media.height, quality, cropParams, media.templates || templates, fallbackFmt, avifQuality)
      }, media);
    });
  }, [additionalMedia]);
  return /*#__PURE__*/React.createElement(LazyWrapper, {
    lazy: lazy,
    nativeLazy: nativeLazy,
    lazyThreshold: lazyThreshold,
    lazyWrapElement: lazyWrapElement
  }, /*#__PURE__*/React.createElement("picture", null, mediaWithSources.map(function (media, index) {
    return /*#__PURE__*/React.createElement(Fragment, {
      key: "".concat(media.sources.srcSet, "-").concat(index)
    }, avifIsEnabled && /*#__PURE__*/React.createElement("source", {
      media: media.query,
      type: "image/avif",
      srcSet: media.sources.srcSetAvif
    }), /*#__PURE__*/React.createElement("source", {
      media: media.query,
      type: "image/webp",
      srcSet: media.sources.srcSet
    }), /*#__PURE__*/React.createElement("source", {
      media: media.query,
      type: "image/pjpeg",
      srcSet: media.sources.srcSetJpeg
    }));
  }), avifIsEnabled && /*#__PURE__*/React.createElement("source", {
    type: "image/avif",
    srcSet: srcSetAvif
  }), /*#__PURE__*/React.createElement("source", {
    type: "image/webp",
    srcSet: srcSet
  }), /*#__PURE__*/React.createElement("source", {
    type: "image/pjpeg",
    srcSet: srcSetJpeg
  }), /*#__PURE__*/React.createElement("img", _extends({
    src: imageSrc,
    width: responsive ? '100%' : width,
    height: responsive ? '100%' : height,
    alt: alt,
    "data-test": dataTest,
    className: className,
    loading: lazy && nativeLazy ? 'lazy' : 'auto'
  }, props))));
};

LazyWrapper.displayName = 'LazyWrapper';
Image.displayName = 'Image';
Image.propTypes = {
  /**
   * Required prop for the Image src
   */
  src: PropTypes.string.isRequired,

  /**
   * Required prop for the Image alt text
   */
  alt: PropTypes.string.isRequired,

  /**
   * Prop for the Image width
   */
  width: PropTypes.number,

  /**
   * Prop for the Image height
   */
  height: PropTypes.number,

  /**
   * Optional prop for the Image quality
   */
  quality: PropTypes.number,

  /**
   * Optional prop for the Image crop parameters object
   */
  cropParams: PropTypes.shape({
    /**
     * Optional prop for the Image x coordinate for cropping
     */
    xCoord: PropTypes.number.isRequired,

    /**
     * Optional prop for the Image y coordinate for cropping
     */
    yCoord: PropTypes.number.isRequired,

    /**
     * Optional prop for the Image crop width
     */
    cropWidth: PropTypes.number.isRequired,

    /**
     * Optional prop for the Image crop height
     */
    cropHeight: PropTypes.number.isRequired
  }),

  /**
   * Optional prop to request images in the AVIF format
   *
   * NOTE: This is an experimental prop as Amplience needs to enable this format first, otherwise images will be returned as jpeg.
   * An A/B test will be performed with AVIF images enabled on specific pages to measure impact
   */
  avifIsEnabled: PropTypes.bool,

  /**
   * Optional prop for the AVIF format image quality (if enabled)
   */
  avifQuality: PropTypes.number,

  /**
   * Optional prop for the fallback format of an image (not webp)
   */
  fallbackFmt: PropTypes.string,

  /**
   * Specify an optional Amplience templates array to be added to the request as POST param
   */
  templates: PropTypes.array,

  /**
   * Specify whether the Image should only load when in viewport
   */
  lazy: PropTypes.bool,

  /**
   * Specify whether the Image should load using browser native lazy load
   */
  nativeLazy: PropTypes.bool,

  /**
   * Number between 0 and 1 indicating the percentage that should be visible before triggering. Can also be an array of numbers, to create multiple trigger points.
   */
  lazyThreshold: PropTypes.number,

  /**
   * The wrapping element for the lazy loaded image
   */
  lazyWrapElement: PropTypes.string,

  /**
   * Specify whether the Image should have 100% height and width
   */
  responsive: PropTypes.bool,

  /**
   * Specify an optional className to be added to your Image
   */
  className: PropTypes.string,

  /**
   * Specify an optional array of additional media, to be used for art direction at different media queries.
   * https://developer.mozilla.org/en-US/docs/Learn/HTML/Multimedia_and_embedding/Responsive_images#art_direction
   *
   * These should be ordered in priority from mobile up to desktop - as the first media query that returns true will be used.
   */
  additionalMedia: PropTypes.arrayOf(PropTypes.shape({
    /**
     * Amplience image source for the media specific image
     */
    src: PropTypes.string.isRequired,

    /**
     * Media query for when this image should be used.
     * For example: "(min-width: 800px)"
     */
    query: PropTypes.string.isRequired,

    /**
     * Image width to be requested from Amplience
     * Can be a string or number as both are handled with parseFloat and used in a template literal in ImageSrcFormat
     * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/parseFloat
     */
    width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

    /**
     * Image height to be requested from Amplience
     */
    height: PropTypes.number
  })),

  /**
   * Specify an optional data-test attribute to be added to your Image for e2e testing
   */
  'data-test': PropTypes.string
};
export default Image;